import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, FileDownload, OpenInNew } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { filterFieldSx } from "../theme/filterStyles";
import {
  pendingEvolutionsService,
  PendingAgeSlice,
  PendingEvolutionsRow,
} from "../services/pendingEvolutionsService";

// Evoluções pendentes por profissional (RN-214). Mostra a EXISTÊNCIA do registro, nunca o
// conteúdo clínico (RN-540): a coordenação vê que falta evolução, não o que ela diria.

const competenciaAtual = (): string => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
};

/** Os 12 últimos meses; a competência é sempre passada ou corrente. */
const COMPETENCIAS = (() => {
  const hoje = new Date();
  const lista: { value: string; label: string }[] = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    lista.push({
      value,
      label: d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
    });
  }
  return lista;
})();

const dataHora = (iso: string): string => {
  const [data, hora] = iso.split("T");
  const [y, m, d] = data.split("-");
  return `${d}/${m}/${y} ${(hora || "").slice(0, 5)}`;
};

const dataCurta = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

/**
 * A cor do atraso é a mesma régua em toda a tela: o número de dias diz sozinho onde agir, sem
 * precisar cruzar com a legenda.
 */
const corDoAtraso = (dias: number): string =>
  dias <= 2 ? "#28a745" : dias <= 7 ? "#fd7e14" : dias <= 30 ? "#dc3545" : "#8b0000";

const corDaAdesao = (percentual: number): string =>
  percentual >= 90 ? "#28a745" : percentual >= 70 ? "#fd7e14" : "#dc3545";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
};

const COLUNAS = [
  { titulo: "Profissional", flex: "1 1 240px", classe: "" },
  { titulo: "Especialidade", flex: "0 0 180px", classe: "c4u-col--lg" },
  { titulo: "Pendentes", flex: "0 0 100px", classe: "" },
  { titulo: "Concluídas", flex: "0 0 100px", classe: "" },
  { titulo: "Total", flex: "0 0 80px", classe: "" },
  { titulo: "% realizado", flex: "0 0 110px", classe: "" },
  { titulo: "Mais antiga", flex: "0 0 120px", classe: "" },
  // Sem titulo no cabecalho (e so o chevron), mas COM rotulo: abaixo de 768px a lista vira
  // cartao e cada celula imprime o proprio `data-label` -- sem ele sobra uma coluna vazia.
  { titulo: "", rotulo: "Fila", flex: "0 0 40px", classe: "" },
];

const ReportPendingEvolutions: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [competence, setCompetence] = useState(competenciaAtual());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [items, setItems] = useState<PendingEvolutionsRow[]>([]);
  const [byAge, setByAge] = useState<PendingAgeSlice[]>([]);
  const [resumo, setResumo] = useState<{
    from: string;
    to: string;
    sessions: number;
    done: number;
    pending: number;
    completionRate: number;
    professionalsWithPending: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  const hasFilterChanges = competence !== competenciaAtual() || !!from || !!to;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pendingEvolutionsService.get({
        competence: competence || undefined,
        from: competence ? undefined : from || undefined,
        to: competence ? undefined : to || undefined,
      });
      setItems(data.items);
      setByAge(data.byAge);
      setResumo({
        from: data.from,
        to: data.to,
        sessions: data.sessions,
        done: data.done,
        pending: data.pending,
        completionRate: data.completionRate,
        professionalsWithPending: data.professionalsWithPending,
      });
    } catch {
      setError("Não foi possível carregar as evoluções pendentes.");
      setItems([]);
      setByAge([]);
      setResumo(null);
    } finally {
      setLoading(false);
    }
  }, [competence, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setCompetence(competenciaAtual());
    setFrom("");
    setTo("");
    setAberto(null);
  };

  /** Competência e período são exclusivos: escolher um limpa o outro, senão o filtro mente. */
  const escolherCompetencia = (valor: string) => {
    setCompetence(valor);
    if (valor) {
      setFrom("");
      setTo("");
    }
    setAberto(null);
  };

  const escolherData = (qual: "from" | "to", valor: string) => {
    setCompetence("");
    if (qual === "from") setFrom(valor);
    else setTo(valor);
    setAberto(null);
  };

  const abrirPaciente = (patientId: string) => {
    const clinic = new URLSearchParams(window.location.search).get("clinic") || "ninho";
    window.open(`${window.location.origin}/?page=patient-register&id=${patientId}&clinic=${clinic}`, "_blank");
  };

  const exportar = async () => {
    try {
      const blob = await pendingEvolutionsService.exportXlsx({
        competence: competence || undefined,
        from: competence ? undefined : from || undefined,
        to: competence ? undefined : to || undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resumo
        ? `evolucoes_pendentes_${resumo.from.slice(0, 10)}_a_${resumo.to.slice(0, 10)}.xlsx`
        : "evolucoes_pendentes.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível gerar a planilha.");
    }
  };

  const totalAtrasos = byAge.reduce((soma, f) => soma + f.count, 0);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Evoluções pendentes
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Sessões que aconteceram e ainda não foram evoluídas. Clique no profissional para
                abrir a fila.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  select
                  size="small"
                  label="Competência"
                  value={competence}
                  onChange={(e) => escolherCompetencia(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Por período</MenuItem>
                  {COMPETENCIAS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="date"
                  size="small"
                  label="Data inicial"
                  value={from}
                  onChange={(e) => escolherData("from", e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="Data final"
                  value={to}
                  onChange={(e) => escolherData("to", e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title="Exportar para Excel" arrow>
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={exportar}
                      disabled={loading || !resumo || resumo.sessions === 0}
                      aria-label="Exportar para Excel"
                    >
                      <FileDownload />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            {resumo && (
              <>
                <Typography sx={{ fontSize: 12, color: "#adb5bd", mb: 1 }}>
                  Período considerado: {dataCurta(resumo.from)} a {dataCurta(resumo.to)}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {[
                    { label: "Sessões realizadas", valor: resumo.sessions, cor: "#0d6e6e", nota: "com presença registrada" },
                    { label: "Evoluções feitas", valor: resumo.done, cor: "#28a745", nota: `${resumo.completionRate}% de registro` },
                    {
                      label: "Pendentes",
                      valor: resumo.pending,
                      cor: resumo.pending > 0 ? "#dc3545" : "#adb5bd",
                      nota: resumo.pending > 0 ? "a registrar" : "nada em aberto",
                    },
                    {
                      label: "Profissionais",
                      valor: resumo.professionalsWithPending,
                      cor: "#6610f2",
                      nota: "com fila aberta",
                    },
                  ].map((k) => (
                    <Paper key={k.label} elevation={0} sx={cardSx}>
                      <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{k.label}</Typography>
                      <Typography
                        sx={{ fontSize: 26, fontWeight: 700, color: k.cor, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}
                      >
                        {k.valor}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>{k.nota}</Typography>
                    </Paper>
                  ))}
                </Box>
              </>
            )}

            {totalAtrasos > 0 && (
              <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Há quanto tempo esperam</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#adb5bd", mb: 1.25 }}>
                  A média esconderia a pendência antiga no meio das recentes — e é a antiga que precisa de ação.
                </Typography>
                {/* Barra empilhada: a proporção entre as faixas é a leitura, não o número solto */}
                <Box sx={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", mb: 1.25 }}>
                  {byAge.map((f, i) => {
                    const cor = [ "#28a745", "#fd7e14", "#dc3545", "#8b0000" ][i] ?? "#adb5bd";
                    const largura = (100 * f.count) / totalAtrasos;
                    return f.count > 0 ? (
                      <Tooltip key={f.label} title={`${f.label}: ${f.count}`} arrow>
                        <Box sx={{ width: `${largura}%`, backgroundColor: cor }} />
                      </Tooltip>
                    ) : null;
                  })}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {byAge.map((f, i) => (
                    <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: ["#28a745", "#fd7e14", "#dc3545", "#8b0000"][i] ?? "#adb5bd",
                        }}
                      />
                      <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                        {f.label}: <strong style={{ color: "#212529" }}>{f.count}</strong>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Typography sx={{ p: 3, fontSize: 14, color: "#dc3545" }}>{error}</Typography>
            ) : (
              <>
                <ResultCount shown={items.length} />

                {items.length === 0 ? (
                  <Typography sx={{ p: 3, fontSize: 14, color: "#6c757d" }}>
                    Nenhuma evolução pendente no período. Todas as sessões com presença registrada
                    já foram evoluídas.
                  </Typography>
                ) : (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <Box className="c4u-table__header">
                      {COLUNAS.map((c, i) => (
                        <Box key={c.titulo || `acao-${i}`} className={`admin-plans-header-cell ${c.classe}`} sx={{ flex: c.flex, minWidth: 0 }}>
                          {c.titulo}
                        </Box>
                      ))}
                    </Box>

                    {items.map((row) => {
                      const expandido = aberto === row.professionalId;
                      return (
                        <React.Fragment key={row.professionalId}>
                          <Box
                            className="c4u-table__row"
                            onClick={() => setAberto(expandido ? null : row.professionalId)}
                            sx={{ cursor: "pointer", backgroundColor: expandido ? "#f1f7f7" : undefined }}
                          >
                            <Box className="admin-plans-cell" data-label="Profissional" sx={{ flex: COLUNAS[0].flex, minWidth: 0 }}>
                              <Typography className="admin-plans-name" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                                {row.professionalName}
                              </Typography>
                            </Box>
                            <Box className={`admin-plans-cell ${COLUNAS[1].classe}`} data-label="Especialidade" sx={{ flex: COLUNAS[1].flex, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5, color: "#6c757d", overflowWrap: "anywhere" }}>
                                {row.professionalTypeName || "—"}
                              </Typography>
                            </Box>
                            <Box className="admin-plans-cell" data-label="Pendentes" sx={{ flex: COLUNAS[2].flex, minWidth: 0 }}>
                              <Chip
                                size="small"
                                label={row.pending}
                                sx={{
                                  height: 22,
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  backgroundColor: `${corDoAtraso(row.oldestDays)}1a`,
                                  color: corDoAtraso(row.oldestDays),
                                }}
                              />
                            </Box>
                            <Box className="admin-plans-cell" data-label="Concluídas" sx={{ flex: COLUNAS[3].flex, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5, color: "#28a745", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                {row.done}
                              </Typography>
                            </Box>
                            <Box className="admin-plans-cell" data-label="Total" sx={{ flex: COLUNAS[4].flex, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5, color: "#6c757d", fontVariantNumeric: "tabular-nums" }}>
                                {row.sessions}
                              </Typography>
                            </Box>
                            <Box className="admin-plans-cell" data-label="% realizado" sx={{ flex: COLUNAS[5].flex, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5, color: corDaAdesao(row.completionRate), fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                {row.completionRate}%
                              </Typography>
                            </Box>
                            <Box className="admin-plans-cell" data-label="Mais antiga" sx={{ flex: COLUNAS[6].flex, minWidth: 0 }}>
                              <Typography
                                sx={{ fontSize: 12.5, color: corDoAtraso(row.oldestDays), fontWeight: row.oldestDays > 7 ? 700 : 400 }}
                              >
                                {row.oldestDays === 0
                                  ? "hoje"
                                  : `há ${row.oldestDays} ${row.oldestDays === 1 ? "dia" : "dias"}`}
                              </Typography>
                            </Box>
                            <Box className="admin-plans-cell" data-label={COLUNAS[7].rotulo} sx={{ flex: COLUNAS[7].flex, minWidth: 0, color: "#6c757d" }}>
                              {expandido ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </Box>
                          </Box>

                          {expandido && (
                            <Box sx={{ backgroundColor: "#fbfcfd", borderTop: "1px solid #e9ecef", px: { xs: 1.5, md: 3 }, py: 1.5 }}>
                              {row.items.map((item) => (
                                <Box
                                  key={item.scheduleId}
                                  sx={{
                                    // Colunas de largura fixa no desktop: paciente, data e atendimento
                                    // alinhados linha a linha. No celular a linha quebra livre.
                                    display: { xs: "flex", md: "grid" },
                                    flexWrap: "wrap",
                                    gridTemplateColumns: "8px 280px 130px minmax(0, 1fr) 72px",
                                    alignItems: "center",
                                    gap: { xs: 0.75, md: 2 },
                                    py: 0.9,
                                    borderBottom: "1px solid #eef1f4",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: corDoAtraso(item.daysWaiting),
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    onClick={() => abrirPaciente(item.patientId)}
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: "#0d6e6e",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.4,
                                      minWidth: { xs: 180, md: 0 },
                                      overflowWrap: "anywhere",
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                  >
                                    {item.patientName}
                                    <OpenInNew sx={{ fontSize: 13 }} />
                                  </Typography>
                                  <Typography sx={{ fontSize: 12.5, color: "#495057", fontVariantNumeric: "tabular-nums" }}>
                                    {dataHora(item.sessionAt)}
                                  </Typography>
                                  {/* Célula sempre presente: sem ela a grade desloca os dias para a coluna do atendimento */}
                                  <Typography sx={{ fontSize: 12, color: "#6c757d", overflowWrap: "anywhere", minWidth: 0 }}>
                                    {item.serviceTypeName}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 11.5,
                                      color: corDoAtraso(item.daysWaiting),
                                      marginLeft: { xs: "auto", md: 0 },
                                      textAlign: "right",
                                      fontVariantNumeric: "tabular-nums",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.daysWaiting === 0
                                      ? "hoje"
                                      : `${item.daysWaiting} ${item.daysWaiting === 1 ? "dia" : "dias"}`}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPendingEvolutions;
